import { ref, getCurrentInstance } from "vue";

export function useGraphUpdater({
  fetchData,
  populateMap,
}) {
  const reloadButtonNeedsToSpin = ref(false);
  const currentYear = ref(new Date().getFullYear());
  const { proxy } = getCurrentInstance();

  let resetTimeoutId = null;
  const DELAY_THRESHOLD = 50;

  const scheduleGraphReset = () => {
    resetTimeoutId = setTimeout(() => setGraphsReady(false), DELAY_THRESHOLD);
  };

  const cancelGraphReset = () => {
    if (resetTimeoutId) {
      clearTimeout(resetTimeoutId);
      resetTimeoutId = null;
    }
  };

  const setGraphsReady = (value) => {
    Object.values(proxy.$refs).forEach((ref) => {
      if (ref?.setReady) {
        ref.setReady(value);
      }
    });
  };

  const populateGraphs = (data) => {
    Object.entries(populateMap).forEach(([refName, { method, getData }]) => {
      const ref = proxy.$refs[refName];
      if (ref && typeof ref[method] === "function") {
        try {
          const payload = getData(data);
          ref[method](payload);
        } catch (err) {
          ref[method](null);
        }
      }
    });
  };

  const handlePopulateError = () => {
    Object.entries(populateMap).forEach(([refName, { method }]) => {
      const ref = proxy.$refs[refName];
      if (ref && typeof ref[method] === "function") {
        ref[method](null);
      }
    });
  };

  const updateData = async (year) => {
    const shouldForceUpdate = currentYear.value === year;
    if (shouldForceUpdate) reloadButtonNeedsToSpin.value = true;

    currentYear.value = year;
    scheduleGraphReset();

    try {
      const data = await fetchData(year, shouldForceUpdate);
      cancelGraphReset();
      populateGraphs(data);
    } catch (err) {
      cancelGraphReset();
      handlePopulateError(); 
    } finally {
      reloadButtonNeedsToSpin.value = false;
    }
  };

  return {
    updateData,
    reloadButtonNeedsToSpin,
    currentYear,
  };
}

export const wrapRequestInYear = async (serviceFn, year, forceUpdate = false) => {
  if (!year) year = new Date().getFullYear();
  const startDate = `${year}-01-01`;
  const endDate = `${year}-12-31`;
  return await serviceFn(startDate, endDate, forceUpdate);
};

export function useRangeGraphUpdater({ fetchData, populateMap }) {
  const reloadButtonNeedsToSpin = ref(false);
  const currentRange = ref(null);
  const { proxy } = getCurrentInstance();

  let resetTimeoutId = null;
  const DELAY_THRESHOLD = 50;

  const scheduleGraphReset = () => {
    resetTimeoutId = setTimeout(() => setGraphsReady(false), DELAY_THRESHOLD);
  };

  const cancelGraphReset = () => {
    if (resetTimeoutId) {
      clearTimeout(resetTimeoutId);
      resetTimeoutId = null;
    }
  };

  const setGraphsReady = (value) => {
    Object.values(proxy.$refs).forEach((ref) => {
      if (ref?.setReady) {
        ref.setReady(value);
      }
    });
  };

  const populateGraphs = (data) => {
    Object.entries(populateMap).forEach(([refName, { method, getData }]) => {
      const ref = proxy.$refs[refName];
      if (ref && typeof ref[method] === "function") {
        try {
          const payload = getData(data);
          ref[method](payload);
        } catch (err) {
          ref[method](null);
        }
      }
    });
  };

  const handlePopulateError = () => {
    Object.entries(populateMap).forEach(([refName, { method }]) => {
      const ref = proxy.$refs[refName];
      if (ref && typeof ref[method] === "function") {
        ref[method](null);
      }
    });
  };

  const sameRange = (a, b) =>
    !!a && !!b && a.from === b.from && a.to === b.to;

  const updateData = async (range) => {
    const shouldForceUpdate = sameRange(currentRange.value, range);
    if (shouldForceUpdate) reloadButtonNeedsToSpin.value = true;

    currentRange.value = range;
    scheduleGraphReset();

    try {
      const data = await fetchData(range, shouldForceUpdate);
      cancelGraphReset();
      populateGraphs(data);
    } catch (err) {
      cancelGraphReset();
      handlePopulateError();
    } finally {
      reloadButtonNeedsToSpin.value = false;
    }
  };

  return {
    updateData,
    reloadButtonNeedsToSpin,
    currentRange,
  };
}

export const wrapRequestInRange = async (serviceFn, from, to, forceUpdate = false, ...rest) => {
  return await serviceFn(from, to, forceUpdate, ...rest);
};